"use client";

import { useEffect, useRef, useState } from "react";

/**
 * A card image that plays a short film on hover.
 *
 * Each "Who it's for" card rests on its audience's END frame (the house as a
 * warm winter home, as a café with a new storey, as an auction, as a scored
 * city) and, on hover, plays the 5–6 s Seedance clip that starts on the plain
 * house (plate 02) and builds to that frame. The film fades in over the poster
 * as it starts, so the jump from the end frame back to the plain house is
 * masked, and it lands exactly where the poster was — the rest state and the
 * end state are the same image. On leave it fades back to the poster.
 *
 * Phones have no hover: there the clip plays once when the card is ~60% in
 * view. Reduced motion never plays. The video is `preload="none"` until the
 * card scrolls near, so a visitor who never reaches the section pays nothing.
 *
 * Assets: public/video/who/<kind>.mp4 (960×720, H.264, muted) and
 * <kind>-poster.jpg, which is the clip's OWN last frame — a poster cut from
 * the source image sat ~1.5% off the generated footage and made the picture
 * jump on hover (see CLAUDE.md § Landing hero, "Who-card hover clips").
 */
export default function HoverClip({
  kind,
  alt,
}: {
  kind: "homebuyer" | "developer" | "broker" | "city";
  alt: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [showing, setShowing] = useState(false);
  const src = `/video/who/${kind}.mp4`;
  const poster = `/video/who/${kind}-poster.jpg`;

  useEffect(() => {
    const video = videoRef.current;
    const wrap = wrapRef.current;
    if (!video || !wrap) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    if (reduce) return;

    // Fetch the clip only once the card is near the viewport; on a phone,
    // also play it once when it is properly on screen.
    let playedOnce = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && video.preload !== "auto") video.preload = "auto";
          if (coarse && e.intersectionRatio >= 0.6 && !playedOnce) {
            playedOnce = true;
            video.currentTime = 0;
            void video.play().then(() => setShowing(true)).catch(() => {});
          }
        }
      },
      { rootMargin: "200px 0px", threshold: [0, 0.6] }
    );
    io.observe(wrap);

    // Hover is the WHOLE card, not just the picture: the card is the link
    // (`.group`), so the clip listens on its closest group ancestor.
    const host: HTMLElement = (wrap.closest(".group") as HTMLElement | null) ?? wrap;
    let leaveTimer = 0;
    const onEnter = () => {
      if (coarse) return;
      window.clearTimeout(leaveTimer);
      video.currentTime = 0;
      void video.play().then(() => setShowing(true)).catch(() => {});
    };
    const onLeave = () => {
      if (coarse) return;
      setShowing(false);
      // Let the fade-out finish before the frame resets under it.
      leaveTimer = window.setTimeout(() => {
        video.pause();
        video.currentTime = 0;
      }, 350);
    };
    host.addEventListener("mouseenter", onEnter);
    host.addEventListener("mouseleave", onLeave);
    return () => {
      io.disconnect();
      host.removeEventListener("mouseenter", onEnter);
      host.removeEventListener("mouseleave", onLeave);
      window.clearTimeout(leaveTimer);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="relative aspect-[4/3] -mx-7 -mt-7 mb-6 overflow-hidden rounded-t-[7px] bg-surface"
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- a plain poster the video fades over */}
      <img src={poster} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        preload="none"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 h-full w-full object-cover transition-opacity duration-300"
        style={{ opacity: showing ? 1 : 0 }}
      />
    </div>
  );
}
